// src/store/api/notificationApi.js

import { baseApi } from './baseApi';
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import signalRService from '../../services/signalrService';

// ===========================
// UNIVERSAL NORMALIZER
// ===========================
const normalizeNotification = (notification) => {

  // case 1: aggregated notifications (likes)
  let users =
    notification.users?.$values ||
    notification.Users?.$values ||
    notification.users ||
    notification.Users ||
    [];

  // case 2: single sender notifications (comment/message)

  if ((!users || users.length === 0) && (notification.sender || notification.Sender)) {
    users = [notification.sender || notification.Sender];
  }

  return {
    id: notification.id ?? notification.Id,
    type: notification.type ?? notification.Type,
    isRead: notification.isRead ?? notification.IsRead,
    count: notification.count ?? notification.Count ?? 1,
    postId: notification.postId ?? notification.PostId,
    sharedPostId: notification.sharedPostId ?? notification.SharedPostId,
    postType: notification.postType ?? notification.PostType,
    commentId: notification.commentId ?? notification.CommentId,
    commentText: notification.commentText ?? notification.CommentText,
    createdAt: notification.createdAt ?? notification.CreatedAt,
    othersCount: notification.othersCount ?? notification.OthersCount ?? 0,
    users: users.filter(Boolean),
  };
};


//   if ((!users || users.length === 0) && notification.sender) {
//     users = [notification.sender];
//   }

//   return {
//     id: notification.id ?? notification.Id,

//     type: notification.type ?? notification.Type,

//     postId: notification.postId ?? notification.PostId,
    
//     postType:
//     notification.postType ?? notification.PostType ?? null,
    
//     commentId:
//       notification.commentId ?? notification.CommentId,

//     isRead:
//       notification.isRead ?? notification.IsRead ?? false,

//     createdAt:
//       notification.createdAt ?? notification.CreatedAt,

//     users: users.map(u => ({
//       id: u.id ?? u.Id,
//       name: u.name ?? u.Name,
//       profilePhotoUrl:
//         u.profilePhotoUrl ??
//         u.ProfilePhotoUrl ??
//         null
//     })),

//     othersCount:
//       notification.othersCount ??
//       notification.OthersCount ??
//       0
//   };
// };



// ===========================
// API
// ===========================

export const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({

    // GET NOTIFICATIONS
    // GET NOTIFICATIONS (Cursor Pagination)
    getNotifications: builder.query({

      query: ({ cursor, pageSize = 10 } = {}) => {
    
        const params = { pageSize };
    
        // only send cursor if it exists
        if (cursor) {
          params.cursor = cursor;
        }
    
        return {
          url: "/notifications/notifications",
          params
        };
      },
    
      transformResponse: (response) => {
    
        const raw =
          response?.items?.$values ||
          response?.items ||
          [];
    
        const notifications = raw.map(normalizeNotification);
    
        return {
          notifications,
          nextCursor: response?.nextCursor ?? null
        };
      },
    
      providesTags: ["Notifications"]
    }),

    // MARK SINGLE READ
    markNotificationAsRead: builder.mutation({
      query: (id) => ({
        url: `/notifications/notifications/${id}/read`,
        method: "POST",
      }),
    
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
    
        const state = getState();
    
        let wasUnread = false;
    
        // get all cached queries
        const queries = notificationApi.util.selectInvalidatedBy(
          state,
          [{ type: 'Notifications' }]
        );
    
        queries.forEach(({ originalArgs }) => {
    
          dispatch(
            notificationApi.util.updateQueryData(
              "getNotifications",
              originalArgs,
              (draft) => {
    
                if (!draft?.notifications) return;
    
                const notif = draft.notifications.find(n => n.id === id);
    
                if (notif && !notif.isRead) {
                  notif.isRead = true;
                  wasUnread = true;
                }
    
              }
            )
          );
    
        });
    
        // update unread count
        dispatch(
          notificationApi.util.updateQueryData(
            "getUnreadCount",
            undefined,
            (draft) => {
              if (wasUnread && draft > 0) {
                return draft - 1;
              }
              return draft;
            }
          )
        );
    
        try {
          await queryFulfilled;
        } catch (err) {
          console.error("mark read failed", err);
        }
      },
    }),
    // MARK ALL READ
    markAllNotificationsAsRead: builder.mutation({
      query: () => ({
        url: `/notifications/notifications/read-all`,
        method: "POST",
      }),
    
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
    
        const patchNotifications = dispatch(
          notificationApi.util.updateQueryData(
            "getNotifications",
            undefined,
            (draft) => {
    
              if (!draft?.notifications) return;
    
              draft.notifications.forEach(n => {
                n.isRead = true;
              });
    
            }
          )
        );
    
        const patchUnreadCount = dispatch(
          notificationApi.util.updateQueryData(
            "getUnreadCount",
            undefined,
            () => 0
          )
        );
    
        try {
          await queryFulfilled;
        } catch {
          patchNotifications.undo();
          patchUnreadCount.undo();
        }
    
      },
    }),

    // UNREAD COUNT
    getUnreadCount: builder.query({
      query: () => `/notifications/notifications/unread-count`,

      transformResponse: (response) => {
        return response?.unreadCount ?? 0;
      },

      providesTags: ['Notifications'],
    }),

  }),
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetUnreadCountQuery,
} = notificationApi;



// ===========================
// SIGNALR HOOK
// ===========================

export const useSignalRNotifications = (currentUserId) => {

  const dispatch = useDispatch();

  const onReceiveNotification = useCallback((notification) => {

    const normalized = normalizeNotification(notification);

    dispatch(
      notificationApi.util.updateQueryData(
        'getNotifications',
        undefined,
        (draft = []) => {

          const existingIndex =
            draft.findIndex(n => n.id === normalized.id);

          if (existingIndex !== -1) {

            draft[existingIndex] = {
              ...draft[existingIndex],
              ...normalized,
            };

          } else {

            draft.unshift(normalized);

          }

        }
      )
    );

    dispatch(
      notificationApi.util.invalidateTags(['Notifications'])
    );

  }, [dispatch]);


  useEffect(() => {

    if (!currentUserId) return;

    // `cancelled` lets the cleanup abort an in-flight wait so we never register
    // the listener after the component has unmounted (or currentUserId changed).
    let cancelled = false;

    const setup = async () => {
      // Bounded wait — give up after ~10s instead of spinning forever if the
      // socket never connects (prevents a runaway 100ms-interval loop).
      const deadline = Date.now() + 10_000;
      while (!signalRService.isConnected()) {
        if (cancelled || Date.now() > deadline) return;
        await new Promise((r) => setTimeout(r, 200));
      }

      if (cancelled) return;

      signalRService.on('ReceiveNotification', onReceiveNotification);
    };

    setup();

    return () => {
      cancelled = true;
      signalRService.off('ReceiveNotification', onReceiveNotification);
    };

  }, [currentUserId, onReceiveNotification]);

  return {
    isConnected: signalRService.isConnected(),
  };

};
